import React, { useState, useEffect } from 'react';

// TypeScript interfaces
interface LoadingFallbackProps {
  message?: string;
  timeout?: number;
  showProgress?: boolean;
  showRetry?: boolean;
  onRetry?: (() => void) | null;
}

/**
 * LoadingFallback - Enhanced loading component with timeout and retry functionality
 * 
 * Provides better user experience during lazy loading by showing progress,
 * handling timeouts, and offering retry mechanisms for failed loads.
 */
const LoadingFallback: React.FC<any> = ({ 
  message = 'Cargando...', 
  timeout = 15000,
  showProgress = true,
  showRetry = true,
  onRetry = null 
}) => {
  const [isTimedOut, setIsTimedOut] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    // Progress animation
    if (showProgress && !isTimedOut) {
      const MAX_PROGRESS_PERCENT = 90; // Stop at 90% to avoid completing before actual load
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 2;
          return newProgress > MAX_PROGRESS_PERCENT ? MAX_PROGRESS_PERCENT : newProgress;
    });
  }, 100);

      return () => clearInterval(progressInterval);
}
    return () => {}; // Return empty cleanup function for other paths
  }, [showProgress, isTimedOut]);

  useEffect(() => {
    // Timeout handler
    const timeoutId = setTimeout(() => {
      setIsTimedOut(true);
}, timeout);

    return () => clearTimeout(timeoutId);
  }, [timeout]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
} else {
      // Default retry - reload the page
      window.location.reload();
}
  };

  if (isTimedOut) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4"
        role="status"
        aria-live="polite"
      >
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
          <div className="mb-4">
            <svg 
              className="mx-auto h-12 w-12 text-yellow-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Carga Lenta
          </h2>
          
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            La aplicación está tardando más de lo esperado en cargar. 
            Esto puede deberse a una conexión lenta.
          </p>
          
          <div className="space-y-3">
            {showRetry && (
              <button
                onClick={handleRetry}
                className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                aria-label="Reintentar carga de la aplicación"
              >
                Reintentar
              </button>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Recargar Página
            </button>
          </div>
          
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            <p>Consejos:</p>
            <ul className="mt-1 text-left space-y-1">
              <li>• Verifica tu conexión a internet</li>
              <li>• Cierra otras pestañas para liberar memoria</li>
              <li>• Intenta en modo incógnito</li>
            </ul>
          </div>
        </div>
      </div>
    );
}

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4"
      role="status"
      aria-live="polite"
    >
      <div className="text-center">
        {/* Loading spinner */}
        <div className="relative mb-4">
          <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 border-t-orange-600 rounded-full animate-spin mx-auto"></div>
          
          {showProgress && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                {Math.round(progress)}%
              </span>
            </div>
          )}
        </div>
        
        {/* Loading message */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {message}
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Preparando tu experiencia de aprendizaje...
        </p>
        
        {/* Progress bar */}
        {showProgress && (
          <div className="w-64 mx-auto">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Progreso de carga: ${Math.round(progress) || undefined}%`}
              ></div>
            </div>
          </div>
        )}
        
        {/* Accessibility content */}
        <span className="sr-only">
          {message} Progreso: {Math.round(progress)} por ciento
        </span>
      </div>
    </div>
  );
};

export default LoadingFallback;